import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaFileAlt,
    FaClock,
    FaCheckCircle,
    FaClipboardList,
    FaEye,
    FaPrint,
    FaAngleLeft,
    FaAngleRight,
    FaCaretDown,
    FaSpinner,
    FaFileExport // Added for export icon
} from 'react-icons/fa';
import * as XLSX from 'xlsx'; // Added for Excel export

export default function InvoiceList() {
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
    const [containerDynamicHeight, setContainerDynamicHeight] = useState('auto');
    const [invoices, setInvoices] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('This Month');
    const itemsPerPage = 10;

    useEffect(() => {
        const handleResize = () => {
            const currentWidth = window.innerWidth;
            const currentHeight = window.innerHeight;

            setIsMobile(currentWidth <= 768);
            setIsSmallMobile(currentWidth <= 480);

            const calculatedHeight = currentHeight - (isMobile ? 40 : 80);
            setContainerDynamicHeight(Math.max(calculatedHeight, 600));
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        const fetchInvoices = async () => {
            setLoading(true);
            try {
                const requestOptions = {
                    method: "GET",
                    redirect: "follow"
                };
                const response = await fetch("https://api.alburaqscents.com/v1/order", requestOptions);
                const result = await response.json();
                if (result.success) {
                    let filteredInvoices = result.data
                        .filter(order => order.orderStatus !== 'orderCancel')
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                    const now = new Date();
                    filteredInvoices = filteredInvoices.filter(invoice => {
                        const createdDate = new Date(invoice.createdAt);
                        if (filter === 'This Month') return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
                        if (filter === 'Last Month') return createdDate.getMonth() === now.getMonth() - 1 && createdDate.getFullYear() === now.getFullYear();
                        if (filter === 'This Year') return createdDate.getFullYear() === now.getFullYear();
                        return true;
                    });

                    setInvoices(filteredInvoices);
                    setTotalPages(Math.ceil(filteredInvoices.length / itemsPerPage));
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobile, filter]);

    const handleViewInvoice = (invoiceId) => {
        navigate(`/invoice/create/${invoiceId}`);
    };

    const handlePrintInvoice = (invoiceId) => {
        console.log('Print Invoice:', invoiceId);
    };

    const handleExport = () => {
        const data = invoices.map(invoice => ({
            'Invoice ID': invoice.orderNumber,
            'Billing Name': invoice.customerId?.name || 'Unknown',
            'Total': invoice.totalAmount,
            'Payment Method': invoice.paymentMethod,
            'Date': new Date(invoice.createdAt).toLocaleDateString(),
            'Status': invoice.orderStatus === 'delivered' ? 'Paid' : 'Unpaid'
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");

        XLSX.writeFile(workbook, `invoices_${filter.replace(/\s/g, '_')}.xlsx`);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInvoices = invoices.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const styles = {
        mainWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%',
            backgroundColor: 'transparent',
            padding: isSmallMobile ? '0px' : (isMobile ? '15px' : '20px'),
            boxSizing: 'border-box',
            fontFamily: 'Inter, sans-serif',
            overflow: 'hidden',
        },
        invoiceListPageContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '1200px',
            height: containerDynamicHeight,
            backgroundColor: 'transparent',
            overflowY: 'auto',
            padding: isSmallMobile ? '15px' : '20px',
            boxSizing: 'border-box',
        },
        headerTitle: {
            fontSize: isSmallMobile ? '20px' : (isMobile ? '24px' : '30px'),
            color: '#000',
            marginBottom: isSmallMobile ? '15px' : '20px',
            fontWeight: 'bold',
            textAlign: 'left',
        },
        cardsContainer: {
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : (isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'),
            gap: isSmallMobile ? '10px' : '15px',
            marginBottom: isSmallMobile ? '15px' : '20px',
        },
        card: {
            backgroundColor: '#000',
            borderRadius: '8px',
            padding: isSmallMobile ? '8px' : '12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            color: '#E5D0A5',
            minHeight: isSmallMobile ? '70px' : '90px',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3px',
        },
        cardTitle: {
            fontSize: isSmallMobile ? '12px' : '14px',
            margin: 0,
        },
        cardIcon: {
            fontSize: isSmallMobile ? '16px' : '20px',
            color: '#E5D0A5',
        },
        cardValue: {
            fontSize: isSmallMobile ? '18px' : '24px',
            fontWeight: 'bold',
            textAlign: 'right',
        },
        receivedOrderContainer: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: isSmallMobile ? '15px' : '20px',
            color: '#E5D0A5',
        },
        receivedOrderHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            textAlign: isSmallMobile ? 'center' : 'left',
        },
        receivedOrderTitle: {
            fontSize: isSmallMobile ? '16px' : '20px',
            margin: 0,
            fontWeight: 'bold',
        },
        monthDropdownContainer: {
            position: 'relative',
            display: 'inline-block',
            width: isSmallMobile ? '100%' : 'auto',
            textAlign: isSmallMobile ? 'center' : 'left',
        },
        monthDropdown: {
            padding: '6px 25px 6px 12px',
            borderRadius: '5px',
            border: '1px solid #E5D0A5',
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '12px',
            cursor: 'pointer',
            appearance: 'none',
            width: isSmallMobile ? '70%' : 'auto',
            boxSizing: 'border-box',
        },
        dropdownArrow: {
            position: 'absolute',
            right: isSmallMobile ? '18%' : '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#E5D0A5',
            pointerEvents: 'none',
            fontSize: '10px',
        },
        exportButton: { // Added style for export button
            backgroundColor: '#E5D0A5',
            color: '#000',
            border: 'none',
            borderRadius: '5px',
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            transition: 'background-color 0.2s ease',
            marginLeft: isSmallMobile ? '0' : '10px',
        },
        exportButtonHover: {
            backgroundColor: '#d0b98e',
        },
        tableWrapper: {
            overflowX: 'auto',
            marginBottom: '15px',
            '::-webkit-scrollbar': {
                display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isMobile ? '700px' : '850px',
        },
        tableHeader: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            fontSize: '12px',
            textAlign: 'left',
            padding: '8px 0',
            borderBottom: '1px solid #333',
        },
        th: {
            padding: '10px',
            whiteSpace: 'nowrap',
        },
        tableRow: {
            borderBottom: '1px solid #333',
        },
        tableRowHover: {
            backgroundColor: '#1a1a1a',
        },
        td: {
            padding: '10px',
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            fontSize: '12px',
        },
        statusPill: {
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            backgroundColor: '#38A169',
            color: '#FFF',
        },
        statusPillPending: {
            backgroundColor: '#FFC107',
            color: '#000',
            padding: '4px 8px',
            borderRadius: '5px',
             fontSize: '10px',
            fontWeight: 'bold',
            textAlign: 'center',
        },
        actionButtons: {
            display: 'flex',
            gap: '4px',
            justifyContent: 'center',
        },
        actionButton: {
            backgroundColor: '#E5D0A5',
            border: 'none',
            borderRadius: '5px',
            padding: '5px 7px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease',
        },
        actionButtonHover: {
            backgroundColor: '#d0b98e',
        },
        actionButtonIcon: {
            fontSize: '13px',
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '15px',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '10px' : '0',
            padding: '0 5px',
        },
        paginationControls: {
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            justifyContent: isSmallMobile ? 'center' : 'flex-start',
        },
        paginationButton: {
            backgroundColor: '#E5D0A5',
            color: '#000',
            border: '1px solid #000',
            borderRadius: '5px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'background-color 0.2s ease',
            minWidth: '25px',
            textAlign: 'center',
        },
        paginationButtonActive: {
            backgroundColor: '#000',
            color: '#E5D0A5',
        },
        paginationButtonHover: {
            backgroundColor: '#d0b98e',
        },
        paginationText: {
            color: '#E5D0A5',
            fontSize: '12px',
            marginTop: isSmallMobile ? '8px' : '0',
        },
        loader: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#E5D0A5',
            fontSize: '17px',
        },
    };

    return (
        <div style={styles.mainWrapper}>
            <div style={styles.invoiceListPageContainer}>
                <h1 style={styles.headerTitle}>Invoice list</h1>

                <div style={styles.cardsContainer}>
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Total Invoice</h3>
                            <FaFileAlt style={styles.cardIcon} />
                        </div>
                        <p style={styles.cardValue}>{invoices.length}</p>
                    </div>

                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>COD Invoice</h3>
                            <FaClock style={styles.cardIcon} />
                        </div>
                        <p style={styles.cardValue}>{invoices.filter(inv => inv.paymentMethod === 'COD').length}</p>
                    </div>

                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Paid Invoice</h3>
                            <FaCheckCircle style={styles.cardIcon} />
                        </div>
                        <p style={styles.cardValue}>{invoices.filter(inv => inv.orderStatus === 'delivered').length}</p>
                    </div>

                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>Pending Invoice</h3>
                            <FaClipboardList style={styles.cardIcon} />
                        </div>
                        <p style={styles.cardValue}>{invoices.filter(inv => inv.orderStatus === 'pending').length}</p>
                    </div>
                </div>

                <div style={styles.receivedOrderContainer}>
                    <div style={styles.receivedOrderHeader}>
                        <h2 style={styles.receivedOrderTitle}>All Received Order</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isSmallMobile ? 'column' : 'row' }}>
                            <div style={styles.monthDropdownContainer}>
                                <select
                                    style={styles.monthDropdown}
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                >
                                    <option value="This Month">This Month</option>
                                    <option value="Last Month">Last Month</option>
                                    <option value="This Year">This Year</option>
                                </select>
                                <FaCaretDown style={styles.dropdownArrow} />
                            </div>
                            <button
                                style={styles.exportButton}
                                onClick={handleExport}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.exportButtonHover.backgroundColor}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.exportButton.backgroundColor}
                            >
                                <FaFileExport /> Export to Excel
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div style={styles.loader}>
                            <FaSpinner className="spin" /> Loading...
                        </div>
                    ) : (
                        <>
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead style={styles.tableHeader}>
                                        <tr>
                                            <th style={styles.th}>Invoice ID</th>
                                            <th style={styles.th}>Billing Name</th>
                                            <th style={styles.th}>Total</th>
                                            <th style={styles.th}>Payment Method</th>
                                            <th style={styles.th}>Date</th>
                                            <th style={styles.th}>Status</th>
                                            <th style={styles.th}>INVOICE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentInvoices.map((invoice, index) => (
                                            <tr
                                                key={invoice._id}
                                                style={styles.tableRow}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <td style={styles.td}>{invoice.orderNumber}</td>
                                                <td style={styles.td}>{invoice.customerId?.name || 'Unknown'}</td>
                                                <td style={styles.td}>{invoice.totalAmount}</td>
                                                <td style={styles.td}>{invoice.paymentMethod}</td>
                                                <td style={styles.td}>{new Date(invoice.createdAt).toLocaleDateString()}</td>
                                                <td style={styles.td}>
                                                    <span style={invoice.orderStatus === 'delivered' ? styles.statusPill : styles.statusPillPending}>
                                                        {invoice.orderStatus === 'delivered' ? 'Paid' : 'Unpaid'}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    <div style={styles.actionButtons}>
                                                        <button
                                                            style={styles.actionButton}
                                                            onClick={() => handleViewInvoice(invoice._id)}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.actionButtonHover.backgroundColor}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.actionButton.backgroundColor}
                                                        >
                                                            <FaEye style={styles.actionButtonIcon} />
                                                        </button>
                                                        <button
                                                            style={styles.actionButton}
                                                            onClick={() => handlePrintInvoice(invoice._id)}
                                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.actionButtonHover.backgroundColor}
                                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.actionButton.backgroundColor}
                                                        >
                                                            <FaPrint style={styles.actionButtonIcon} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div style={styles.paginationContainer}>
                                <div style={styles.paginationControls}>
                                    <button
                                        style={styles.paginationButton}
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.paginationButtonHover.backgroundColor}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.paginationButton.backgroundColor}
                                    >
                                        <FaAngleLeft />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                        <button
                                            key={number}
                                            style={currentPage === number ? { ...styles.paginationButton, ...styles.paginationButtonActive } : styles.paginationButton}
                                            onClick={() => paginate(number)}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.paginationButtonHover.backgroundColor}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = (currentPage === number ? styles.paginationButtonActive.backgroundColor : styles.paginationButton.backgroundColor)}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                    <button
                                        style={styles.paginationButton}
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.paginationButtonHover.backgroundColor}
                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.paginationButton.backgroundColor}
                                    >
                                        <FaAngleRight />
                                    </button>
                                </div>
                                <p style={styles.paginationText}>{invoices.length} Results</p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}